import { useState } from 'react'
import { useSQLiteQuery } from '../lib/reactive'
import {
  archiveNote,
  unarchiveNote,
  getNote,
  enqueueSyncOp,
  type LocalNote,
} from '../lib/db'
import { rowToLocalNote, type NoteRow } from '../lib/repositories'
import { query } from '../lib/sqlite'
import { track, AnalyticsEvent } from '../lib/analytics'

/** Archived notes — archived but not trashed. Trash wins visually, so a note
 *  that is both archived and deleted lives in the Trash view, not here. */
export function useArchiveNotes(): LocalNote[] {
  return useSQLiteQuery(
    () => {
      const rows = query<NoteRow>(
        'SELECT * FROM notes WHERE archived_at IS NOT NULL AND deleted_at IS NULL ORDER BY archived_at DESC',
      )
      return rows.map(rowToLocalNote)
    },
    [],
    ['notes'],
    [] as LocalNote[],
  ) ?? []
}

export function useArchiveNote() {
  const [isPending, setIsPending] = useState(false)

  const mutate = async (id: string, source = 'note_actions_menu'): Promise<void> => {
    setIsPending(true)
    try {
      // Capture updated_at before the metadata write so the sync op carries the
      // note's real version for conflict detection (archive must not bump it).
      const expectedUpdatedAt = getNote(id)?.updatedAt
      archiveNote(id)
      // Read back the timestamp the local write stored so the pushed op and the
      // local row agree on archived_at exactly.
      const archivedAt = getNote(id)?.archivedAt ?? new Date().toISOString()
      // Archive rides the generic 'update' op (mirrors pin) — no new op type.
      enqueueSyncOp({
        type: 'update',
        entity: 'note',
        entityId: id,
        data: { archived_at: archivedAt, expected_updated_at: expectedUpdatedAt },
        timestamp: Date.now(),
        retries: 0,
      })
      track(AnalyticsEvent.NoteAction, { action: 'archive', source })
    } finally {
      setIsPending(false)
    }
  }

  return { mutate, isPending }
}

export function useUnarchiveNote() {
  const [isPending, setIsPending] = useState(false)

  const mutate = async (id: string, source = 'archive_view'): Promise<void> => {
    setIsPending(true)
    try {
      const expectedUpdatedAt = getNote(id)?.updatedAt
      unarchiveNote(id)
      // Explicit null clears archived_at server-side (the worker handles null
      // via an `in` check, since `??` would swallow it).
      enqueueSyncOp({
        type: 'update',
        entity: 'note',
        entityId: id,
        data: { archived_at: null, expected_updated_at: expectedUpdatedAt },
        timestamp: Date.now(),
        retries: 0,
      })
      track(AnalyticsEvent.NoteAction, { action: 'unarchive', source })
    } finally {
      setIsPending(false)
    }
  }

  return { mutate, isPending }
}
